import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, DollarSign, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TourDetail = () => {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/content/tours/${slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => setTour(data))
      .catch(() => setTour(null))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-700" />
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">Tour not found</h2>
          <Link to="/"><Button className="bg-emerald-800 hover:bg-emerald-900">Return Home</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${tour.heroImage})`, transform: `translateY(${scrollY * 0.5}px)` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70" />
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <div className="inline-block bg-amber-600 text-white px-4 py-2 text-sm font-semibold mb-6 rounded-sm">{tour.category}</div>
          <h1 className="text-5xl md:text-7xl font-serif text-white mb-6" style={{ textShadow: '2px 2px 20px rgba(0,0,0,0.7)' }}>{tour.title}</h1>
          <p className="text-xl text-white/90 mb-8">{tour.shortDescription}</p>
          <div className="flex gap-6 justify-center flex-wrap text-white">
            <div className="flex items-center gap-2 bg-black/30 px-4 py-2 rounded-sm backdrop-blur-sm"><Calendar className="w-5 h-5" /><span>{tour.duration}</span></div>
            <div className="flex items-center gap-2 bg-black/30 px-4 py-2 rounded-sm backdrop-blur-sm"><MapPin className="w-5 h-5" /><span>{tour.location}</span></div>
            {tour.price && <div className="flex items-center gap-2 bg-black/30 px-4 py-2 rounded-sm backdrop-blur-sm"><DollarSign className="w-5 h-5" /><span className="font-semibold">{tour.currency} {tour.price} per person</span></div>}
          </div>
        </div>
      </section>

      <div className="bg-white border-b border-zinc-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link to="/"><Button variant="ghost" className="hover:bg-zinc-100"><ArrowLeft className="mr-2 w-4 h-4" /> Back to All Journeys</Button></Link>
        </div>
      </div>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-serif mb-8 text-zinc-900">Tour Overview</h2>
          <div className="w-20 h-1 bg-amber-600 mb-8" />
          <p className="text-lg text-zinc-700 leading-relaxed mb-8">{tour.overview}</p>
          {tour.highlights?.length > 0 && (
            <div>
              <h3 className="text-2xl font-semibold mb-6 text-zinc-900">Highlights</h3>
              <ul className="space-y-3">
                {tour.highlights.map((highlight, index) => (
                  <li key={index} className="flex items-start gap-3 text-zinc-700">
                    <Check className="w-6 h-6 text-emerald-700 flex-shrink-0 mt-1" />
                    <span className="text-lg">{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {tour.destinations && (
        <section className="py-20 px-4 bg-zinc-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-serif mb-8 text-zinc-900 text-center">Explore Our Destinations</h2>
            <div className="w-20 h-1 bg-amber-600 mb-16 mx-auto" />
            <div className="grid md:grid-cols-3 gap-8">
              {tour.destinations.map((dest, index) => (
                <Card key={index} className="overflow-hidden group hover:shadow-xl transition-all duration-500 border-0 rounded-sm">
                  <div className="relative h-64 overflow-hidden">
                    <img src={dest.image} alt={dest.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-2xl font-serif text-white mb-1">{dest.name}</h3>
                      <p className="text-white/80 text-sm">{dest.description}</p>
                    </div>
                  </div>
                  <CardContent className="p-6 bg-white">
                    <ul className="space-y-2">
                      {dest.highlights.map((h, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-zinc-600">
                          <Check className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" /><span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {tour.itinerary?.length > 0 && (
        <section className="py-20 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-serif mb-8 text-zinc-900">Detailed Itinerary</h2>
            <div className="w-20 h-1 bg-amber-600 mb-12" />
            <Accordion type="single" collapsible className="space-y-4">
              {tour.itinerary.map((day, index) => (
                <AccordionItem key={index} value={`day-${index}`} className="border-2 border-zinc-200 rounded-sm overflow-hidden hover:border-emerald-700 transition-colors duration-300">
                  <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-zinc-50 transition-colors">
                    <div className="flex items-center gap-4 text-left">
                      <div className="bg-emerald-800 text-white w-12 h-12 rounded-full flex items-center justify-center font-semibold flex-shrink-0">{day.day}</div>
                      <span className="text-lg font-semibold text-zinc-900">{day.title}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 py-4 bg-zinc-50 border-t border-zinc-200">
                    <p className="text-zinc-700 leading-relaxed pl-16">{day.description}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      <section className="py-20 px-4 bg-zinc-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-serif mb-6 text-zinc-900">What's Included</h2>
              <div className="w-16 h-1 bg-emerald-700 mb-8" />
              <ul className="space-y-3">
                {tour.inclusions?.map((item, index) => (
                  <li key={index} className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-1" /><span className="text-zinc-700">{item}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-serif mb-6 text-zinc-900">What's Not Included</h2>
              <div className="w-16 h-1 bg-red-600 mb-8" />
              <ul className="space-y-3">
                {tour.exclusions?.map((item, index) => (
                  <li key={index} className="flex items-start gap-3"><X className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" /><span className="text-zinc-700">{item}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-emerald-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-serif mb-6">Ready to Begin Your Journey?</h2>
          <p className="text-xl mb-10 text-white/90">Contact us today to book this extraordinary adventure</p>
          <div className="flex gap-6 justify-center flex-wrap">
            <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-10 py-6 text-lg rounded-sm transition-all duration-300 hover:scale-105" onClick={() => { window.location.href = '/#contact'; }}>
              Book This Journey
            </Button>
            <Link to="/"><Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-emerald-900 px-10 py-6 text-lg rounded-sm transition-all duration-300">Explore More Tours</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TourDetail;
